import React from 'react';

import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { MdWarning } from 'react-icons/md';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';

import type { MembershipRegistration } from '@appTypes/esb';
import type { ESBBaseError } from '@appTypes/esb/errors';
import { useUpdateMembershipRegistrationMutation } from '@modules/rtk/esb-api';
import type { FormInstance } from '@modules/shared/components/Form/Form.types';
import { Space } from '@modules/shared/components/Space/Space';
import toastService from '@modules/toasts/service';
import {
  Button,
  Checkbox,
  Col,
  ConditionalRender,
  Form,
  Modal,
  Radio,
  Row,
} from '@shared/components';
import { intl } from '@shared/localization';

import styles from './EditParticipationModal.less';

interface EditParticipationModalProps {
  isOptedOut: boolean;
  visible: boolean;
  onCancel: () => void;
  personGuid: string;
  registration: Pick<
    MembershipRegistration,
    'registrationId' | 'renewalStatus' | 'organizationName' | 'bsaOrderId'
  >;
}

const EditParticipationModalWithForm = (
  props: EditParticipationModalProps & {
    form: FormInstance;
  },
) => {
  const { isOptedOut, form, registration, personGuid, onCancel } = props;
  const [updateMembership, { isLoading }] =
    useUpdateMembershipRegistrationMutation();

  const onSubmit = () => {
    form.validateFields((errors /* values */) => {
      if (errors) return;

      // TODO: right now we don't have support for the 1st option
      //const method = values.optOutMethod;
      async function update() {
        try {
          await updateMembership({
            personGuid: personGuid,
            body: { isAutoRenewalOptedOut: true },
            registrationId: registration.registrationId,
          }).unwrap();
          toastService.success(
            intl.formatMessage({
              id: 'youthMembership.optoutModal.update.success',
            }),
          );
          onCancel();
        } catch (error) {
          const err = error as FetchBaseQueryError;
          const data = err.data as ESBBaseError | undefined;
          if (err.status === 403) return;
          const errorMessage = data?.errorDesc;
          toastService.error(errorMessage);
        }
      }

      update();
    });
  };
  const valid = Object.values(form.getFieldsError()).every(error => !error);

  const hasPendingOrder = !!registration.bsaOrderId;

  return (
    <Modal
      visible={props.visible}
      onCancel={() => onCancel()}
      title="Edit Participation Status"
      footer={
        <Row type="flex" justify="end">
          <Col>
            <Space>
              <ConditionalRender value={isOptedOut || hasPendingOrder}>
                <Button
                  shape="round"
                  type="primary"
                  size="large"
                  onClick={onCancel}
                >
                  <FormattedMessage id="shared.close" />
                </Button>
              </ConditionalRender>
              <ConditionalRender value={!isOptedOut && !hasPendingOrder}>
                <Button
                  shape="round"
                  type="primary"
                  loading={isLoading}
                  size="large"
                  onClick={onSubmit}
                  disabled={!valid}
                >
                  <FormattedMessage id="shared.confirm" />
                </Button>
                <Button
                  disabled={isLoading}
                  shape="round"
                  size="large"
                  onClick={onCancel}
                >
                  <FormattedMessage id="shared.cancel" />
                </Button>
              </ConditionalRender>
            </Space>
          </Col>
        </Row>
      }
    >
      <Form>
        <ConditionalRender value={!isOptedOut && hasPendingOrder}>
          <p>
            <FormattedHTMLMessage id="youthMembership.optoutModal.withPendingOrder" />
          </p>
        </ConditionalRender>
        <ConditionalRender value={!isOptedOut && !hasPendingOrder}>
          <p>
            <FormattedHTMLMessage id="youthMembership.optoutModal.section-1" />
          </p>
          <div className={styles.checkboxes}>
            <Form.Item name="checks">
              {form.getFieldDecorator('checks', {
                rules: [
                  {
                    async validator(_rule, value) {
                      const values = new Set(value);
                      const expectedValues = ['check-1', 'check-2', 'check-3'];
                      const isValid = expectedValues.every(item =>
                        values.has(item),
                      );
                      if (!isValid) {
                        throw new Error(
                          intl.formatMessage({
                            id: 'youthMembership.optoutModal.acknowledge',
                          }),
                        );
                      }
                    },
                  },
                ],
              })(
                <Checkbox.Group>
                  <Checkbox value="check-1" newStyle>
                    <FormattedHTMLMessage id="youthMembership.optoutModal.checkbox-1" />
                  </Checkbox>
                  <Checkbox value="check-2" newStyle>
                    <FormattedHTMLMessage id="youthMembership.optoutModal.checkbox-2" />
                  </Checkbox>
                  <Checkbox value="check-3" newStyle>
                    <FormattedHTMLMessage id="youthMembership.optoutModal.checkbox-3" />
                  </Checkbox>
                </Checkbox.Group>,
              )}
            </Form.Item>
          </div>
          <p>
            <FormattedHTMLMessage id="youthMembership.optoutModal.section-2" />
          </p>
          <p>
            <strong>
              <MdWarning className={styles.warningIcon} />{' '}
              <FormattedHTMLMessage id="youthMembership.optoutModal.section-3" />
            </strong>
          </p>
          <div>
            <Form.Item name="optOutMethod">
              {form.getFieldDecorator('optOutMethod', {
                initialValue: 'immediately',
              })(
                <Radio.Group>
                  <Radio disabled value="endOfTerm">
                    <FormattedMessage id="youthMembership.optoutModal.method-1" />
                  </Radio>
                  <Radio value={'immediately'}>
                    <FormattedMessage id="youthMembership.optoutModal.method-2" />
                  </Radio>
                </Radio.Group>,
              )}
            </Form.Item>
          </div>
        </ConditionalRender>
        <ConditionalRender value={isOptedOut}>
          <p>
            <FormattedHTMLMessage
              id="youthMembership.optoutModal.alreadyOptedOut"
              values={{ unitName: registration.organizationName }}
            />
          </p>
        </ConditionalRender>
      </Form>
    </Modal>
  );
};

const EditParticipationModal = Form.create()(
  EditParticipationModalWithForm,
) as unknown as React.FC<EditParticipationModalProps>;

export default EditParticipationModal;
